import { useAuthContext, useIsHydrated } from '@core/shared';
import { OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, Input } from '@core/ui';
import { useMutation } from '@tanstack/react-query';
import { MouseEvent, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useValidations, useSubmitValidation } from '../../..';
import { AccountWizardState } from '../../../types';
import { PasswordEyeIcon } from '../../atoms';

interface Props {
    state?: AccountWizardState;
    step?: 'email' | 'password';
    onAuthorized?: () => void;
    onRegister: (emailAddress: string) => void;
    onForgotPassword: (emailAddress: string) => void;
    onStepChange?: (step: 'email' | 'password', state: AccountWizardState) => void;
}

export const Login = ({ state: initialState, step: initialStep, onAuthorized, onRegister, onForgotPassword, onStepChange }: Props) => {
    const { t } = useTranslation();
    const {
        watch,
        register,
        formState: { errors },
        getValues,
        handleSubmit,
        trigger
    } = useForm<AccountWizardState>({ mode: 'onSubmit', reValidateMode: 'onSubmit', defaultValues: initialState ?? {} });
    const validate = useSubmitValidation({ watch, trigger });
    const onSubmit: SubmitHandler<AccountWizardState> = () => {
        if (step === 'email') {
            validateEmail({ preventDefault: () => {}, stopPropagation: () => {} } as MouseEvent<HTMLButtonElement>);
        } else {
            login({ preventDefault: () => {}, stopPropagation: () => {} } as MouseEvent<HTMLButtonElement>);
        }
    };

    const [step, setStep] = useState<'email' | 'password'>(initialStep ?? 'email');
    const [passwordType, setPasswordType] = useState('password');

    const { updateAuthState } = useAuthContext();
    const isHydrated = useIsHydrated();
    const { emailValidation, passwordRequired } = useValidations();

    const togglePasswordVisibility = () => setPasswordType((prev) => (prev === 'password' ? 'text' : 'password'));

    useEffect(() => {
        if (onStepChange) onStepChange(step, getValues());
    }, [step]);

    const validateEmailMutation = useMutation(OptionsApi.login.exists());
    const validateEmail = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        validate(['emailAddress']).then((isValid) => {
            if (!isValid) return;
            const { emailAddress } = getValues();
            if (!emailAddress) return;
            validateEmailMutation.mutate(emailAddress, {
                onSuccess: (response) => {
                    if (response) setStep('password');
                    else onRegister(emailAddress);
                }
            });
        });
    };

    const loginMutation = useMutation(OptionsApi.login.login());
    const login = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        validate(['emailAddress', 'password']).then((isValid) => {
            if (!isValid) return;
            const { emailAddress, password } = getValues();
            loginMutation.mutate(
                { username: emailAddress, password },
                {
                    onSuccess: (response) => {
                        updateAuthState(response);
                        if (onAuthorized) onAuthorized();
                    }
                }
            );
        });
    };

    const handleForgotPassword = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        onForgotPassword(getValues().emailAddress ?? '');
    };

    const back = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();

        setStep('email');
        loginMutation.reset();
        validateEmailMutation.reset();
    };

    const error = validateEmailMutation.error ?? loginMutation.error;
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {error && <ErrorMessage errorSubject="Account.ApiErrorCode" apiError={error} classNames="mb-4" />}
            <Input
                autoFocus
                type="email"
                label={t('Account.Label.Email')}
                readOnly={step === 'password'}
                disabled={step === 'password'}
                {...register('emailAddress', emailValidation)}
                errorMessage={errors.emailAddress?.message}
                autoComplete="email"
            />
            <div style={{ display: step === 'email' ? 'none' : 'block' }}>
                <Input
                    type={passwordType}
                    label={t('Account.Label.Password')}
                    {...register('password', passwordRequired)}
                    errorMessage={errors.password?.message}
                    autoComplete="current-password"
                >
                    <PasswordEyeIcon passwordType={passwordType} onClick={togglePasswordVisibility} />
                </Input>
            </div>
            {step === 'email' && (
                <>
                    <Button
                        data-testid="login-next"
                        type="submit"
                        isLoading={validateEmailMutation.isPending}
                        disabled={!isHydrated}
                        onClick={validateEmail}
                        classNames="w-full"
                        variant="primary"
                    >
                        {t('Account.Button.Next')}
                    </Button>
                </>
            )}
            {step === 'password' && (
                <>
                    <div className="flex flex-wrap gap-4 clear-both mb-8">
                        <Button type="button" isLoading={loginMutation.isPending} onClick={back} variant="primary-outline" classNames="flex-1">
                            {t('Account.Button.Back')}
                        </Button>
                        <Button
                            data-testid="login-submit"
                            type="submit"
                            isLoading={loginMutation.isPending}
                            disabled={!isHydrated}
                            onClick={login}
                            variant="primary"
                            classNames="flex-1"
                        >
                            {t('Account.Button.Login')}
                        </Button>
                    </div>
                    <Button
                        data-testid="login-forgot-password"
                        type="button"
                        isLoading={loginMutation.isPending}
                        disabled={!isHydrated}
                        className="text-primary underline hover:underline decoration-primary decoration-2"
                        onClick={handleForgotPassword}
                    >
                        {t('Account.Button.ForgotPassword')}
                    </Button>
                </>
            )}
        </form>
    );
};
